import { Fragment, useState, type ReactNode } from 'react';

/** Eigene kleine Symbole (24×24, Strich, Farbe folgt dem Text). */
const S = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

export const Zeichen = {
  lupe: (
    <S>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </S>
  ),
  hoch: (
    <S>
      <path d="M7 11v9H4v-9zM7 11l4-7c1.4 0 2.2 1 2 2.3L12.5 10H18a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 16.8 20H7" />
    </S>
  ),
  runter: (
    <S>
      <path d="M7 13V4H4v9zM7 13l4 7c1.4 0 2.2-1 2-2.3L12.5 14H18a2 2 0 0 0 2-2.3l-1.2-6A2 2 0 0 0 16.8 4H7" />
    </S>
  ),
  kopieren: (
    <S>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </S>
  ),
  stift: (
    <S>
      <path d="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" />
    </S>
  ),
  pfeil: (
    <S>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </S>
  ),
  stern: (
    <S>
      <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
    </S>
  ),
  x: (
    <S>
      <path d="M6 6l12 12M18 6L6 18" />
    </S>
  ),
  senden: (
    <S>
      <path d="M4 12l16-8-6 16-2.5-6.5z" />
    </S>
  ),
  ordner: (
    <S>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </S>
  ),
  leute: (
    <S>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19c.6-3.2 3-5 6-5s5.4 1.8 6 5M16 5.5a3 3 0 0 1 0 5.6M18 14c1.6.6 2.7 2.2 3 5" />
    </S>
  ),
  schloss: (
    <S>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </S>
  ),
};

/** **fett** innerhalb einer Zeile */
function zeile(t: string): ReactNode[] {
  return t.split(/(\*\*[^*]+\*\*)/g).map((teil, i) =>
    teil.startsWith('**') && teil.endsWith('**') && teil.length > 4 ? <strong key={i}>{teil.slice(2, -2)}</strong> : <Fragment key={i}>{teil}</Fragment>,
  );
}

/**
 * Zeigt KI-Text: Absätze, Listen (- / * / 1.), Überschriften (#) und **fett**.
 * Adressen bleiben reiner Text – nie anklickbar, nichts wird geladen.
 */
export function KiText({ text }: { text: string }) {
  const bloecke: ReactNode[] = [];
  let liste: { geordnet: boolean; punkte: string[] } | null = null;
  let absatz: string[] = [];
  const absatzFertig = () => {
    if (absatz.length) bloecke.push(<p key={bloecke.length}>{absatz.flatMap((z, i) => (i ? [<br key={`b${i}`} />, ...zeile(z)] : zeile(z)))}</p>);
    absatz = [];
  };
  const listeFertig = () => {
    if (liste) {
      const Tag = liste.geordnet ? 'ol' : 'ul';
      bloecke.push(
        <Tag key={bloecke.length}>
          {liste.punkte.map((p, i) => (
            <li key={i}>{zeile(p)}</li>
          ))}
        </Tag>,
      );
    }
    liste = null;
  };
  for (const roh of text.replace(/\r/g, '').split('\n')) {
    const z = roh.trimEnd();
    const punkt = z.match(/^\s*(?:[-*•]|(\d+)[.)])\s+(.*)$/);
    const titel = z.match(/^\s*#{1,4}\s+(.*)$/);
    if (!z.trim()) {
      absatzFertig();
      listeFertig();
    } else if (punkt) {
      absatzFertig();
      const geordnet = !!punkt[1];
      if (!liste || liste.geordnet !== geordnet) {
        listeFertig();
        liste = { geordnet, punkte: [] };
      }
      liste.punkte.push(punkt[2]!);
    } else if (titel) {
      absatzFertig();
      listeFertig();
      bloecke.push(<h4 key={bloecke.length}>{zeile(titel[1]!)}</h4>);
    } else {
      listeFertig();
      absatz.push(z);
    }
  }
  absatzFertig();
  listeFertig();
  return <div className="id-kitext">{bloecke}</div>;
}

/** Adresse als kopierbarer Text – bewusst kein Link (Marcs Vorgabe). */
export function Adresse({ url }: { url: string }) {
  const [kopiert, setKopiert] = useState(false);
  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Ältere Browser / ohne HTTPS: Text markieren, damit man ihn selbst kopieren kann
      const feld = document.createElement('textarea');
      feld.value = url;
      document.body.appendChild(feld);
      feld.select();
      document.execCommand('copy');
      feld.remove();
    }
    setKopiert(true);
    setTimeout(() => setKopiert(false), 1500);
  };
  return (
    <span className="id-adresse">
      <code title="Adresse nur als Text – zum Öffnen selbst kopieren">{url}</code>
      <button type="button" className="knopf knopf-leise knopf-klein" onClick={() => void kopieren()} title="Adresse kopieren">
        {Zeichen.kopieren} {kopiert ? 'kopiert' : 'kopieren'}
      </button>
    </span>
  );
}

export function uhrzeit(iso: string): string {
  const d = new Date(iso);
  const heute = new Date();
  const gleich = d.toDateString() === heute.toDateString();
  return gleich
    ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
